import type { IDocument } from "@bbj-types/document";
import type { ObjectId } from "@bbj-types/utils";
import { ArrowDropDown } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { Button, CircularProgress } from "@mui/material";
import { type ChangeEventHandler, useRef, useState } from "react";
import { INPUT_FILE_ALLOWED_MIME } from "../logics";
import { DocumentOrigin, UploadFileFieldPopoverOrigin } from "./popoverOrigin";
import { UploadFileFieldPopoverSelectableDocuments } from "./popoverSelectableDocuments";

export interface IUploadFileFieldSelectProps {
  label: string;
  loading: boolean;
  documents: IDocument[];
  selectableDocuments: IDocument[];
  labelSelectFromSelectableDocuments: string;
  labelSelectFromComputer: string;
  onInputFileChange: ChangeEventHandler<HTMLInputElement>;
  onSelectedDocuments: (selectedDocuments: Set<ObjectId>) => void;
}

export const UploadFileFieldSelect = ({
  label,
  loading,
  documents,
  selectableDocuments,
  labelSelectFromSelectableDocuments,
  labelSelectFromComputer,
  onInputFileChange,
  onSelectedDocuments,
}: IUploadFileFieldSelectProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const anchorElRef = useRef<HTMLButtonElement>(null);
  const [popoverOriginOpened, setPopoverOriginOpened] = useState(false);
  const [popoverSelectableDocumentsOpened, setPopoverSelectableDocumentsOpened] = useState(false);
  const hasSelectableDocuments = selectableDocuments.length > 0;

  const handleButtonClick = () => {
    if (hasSelectableDocuments) {
      setPopoverOriginOpened(true);
    } else {
      inputFileRef.current?.click();
    }
  };

  const handleSelectDocumentOrigin = (origin: DocumentOrigin) => {
    if (origin === DocumentOrigin.COMPUTER) {
      inputFileRef.current?.click();
    } else {
      setPopoverSelectableDocumentsOpened(true);
    }
    setPopoverOriginOpened(false);
  };

  const handleCloseDocumentOrigin = () => {
    setPopoverOriginOpened(false);
  };

  const handleSelectSelectableDocuments = (documents: Set<ObjectId>) => {
    onSelectedDocuments(documents);
    setPopoverSelectableDocumentsOpened(false);
  };

  const handleCloseSelectableDocuments = () => {
    setPopoverSelectableDocumentsOpened(false);
  };

  return (
    <span>
      <input
        type="file"
        style={{ display: "none" }}
        onChange={onInputFileChange}
        accept={INPUT_FILE_ALLOWED_MIME}
        multiple
        ref={inputFileRef}
      />
      <Button
        variant="outlined"
        startIcon={!hasSelectableDocuments && <AddIcon />}
        endIcon={hasSelectableDocuments && <ArrowDropDown />}
        onClick={handleButtonClick}
        size="small"
        ref={anchorElRef}
        {...(loading && { disabled: true, endIcon: <CircularProgress color="inherit" size="16px" /> })}
      >
        {label}
      </Button>
      <UploadFileFieldPopoverOrigin
        open={popoverOriginOpened}
        anchorEl={anchorElRef.current}
        labelSelectFromSelectableDocuments={labelSelectFromSelectableDocuments}
        labelSelectFromComputer={labelSelectFromComputer}
        onSelectDocumentOrigin={handleSelectDocumentOrigin}
        onClose={handleCloseDocumentOrigin}
      />
      <UploadFileFieldPopoverSelectableDocuments
        open={popoverSelectableDocumentsOpened}
        anchorEl={anchorElRef.current}
        documents={documents}
        selectableDocuments={selectableDocuments}
        onSubmit={handleSelectSelectableDocuments}
        onClose={handleCloseSelectableDocuments}
      />
    </span>
  );
};
